/**
 * PlaylistDetail — v3.0.135 (Phase 2 + 3)
 * Owner-facing detail view. Playback is now handled by the global
 * PlayerContext + GlobalPlayer bar mounted in App.js, so listeners
 * can keep playing while they navigate away.
 *
 * Also adds:
 *   - Share dialog (Phase 3): toggle public, copy /p/:id?code=…
 *   - Cover image on the header when the auto-collage is ready
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Play, Pause, Music, X, Loader2, ListMusic, Globe, Lock,
  Share2, Copy, Check, GripVertical, Headphones, ImageIcon, RotateCcw,
  Users, Mail, MessageSquare, QrCode, Send, Trash2, Pencil,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import {
  ResponsiveContainer, BarChart, Bar, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { apiFetch, getStoredToken } from '../App';
import { usePlayer } from '../context/PlayerContext';
import MainNav from '../components/MainNav';
import CoverCropperDialog from '../components/CoverCropperDialog';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlaylistDetail() {
  const { playlistId } = useParams();
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [savingShare, setSavingShare] = useState(false);
  const [savingReorder, setSavingReorder] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [resettingCover, setResettingCover] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const coverInputRef = useRef(null);
  // v3.0.146 — Share dialog extras
  const [showQr, setShowQr] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  // v3.0.149 — inline rename (share link + access code are preserved)
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [savingRename, setSavingRename] = useState(false);

  const {
    playQueue, playAtIndex, togglePlay, playing, current, playlistId: liveQueueId,
  } = usePlayer();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch(`/playlists/${playlistId}`);
      setPlaylist(data);
    } catch (err) {
      toast.error(`Load failed: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [playlistId]);

  const songs = playlist?.songs || [];
  const isLiveQueue = liveQueueId === playlistId;
  const nowPlayingId = isLiveQueue ? current?.song_id : null;

  const startPlayback = (startIndex = 0) => {
    if (!songs.length) return;
    playQueue(songs, {
      startIndex,
      title: playlist?.name || 'Playlist',
      playlistId,
      autoplay: true,
    });
  };

  const onRowClick = (i) => {
    if (isLiveQueue) {
      playAtIndex(i);
    } else {
      startPlayback(i);
    }
  };

  const handleRemove = async (songId) => {
    if (!window.confirm('Remove this song from the playlist?')) return;
    try {
      const { data: updated } = await apiFetch(
        `/playlists/${playlistId}/songs/${songId}`, { method: 'DELETE' },
      );
      setPlaylist(updated);
      toast.success('Removed from playlist');
    } catch (err) {
      toast.error(`Remove failed: ${err.message || err}`);
    }
  };

  const togglePublic = async (nextValue) => {
    setSavingShare(true);
    try {
      const { data } = await apiFetch(`/playlists/${playlistId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_public: nextValue }),
      });
      setPlaylist((prev) => ({ ...(prev || {}), ...data }));
      toast.success(nextValue ? 'Playlist is now shareable' : 'Playlist is private again');
    } catch (err) {
      toast.error(`Update failed: ${err.message || err}`);
    } finally {
      setSavingShare(false);
    }
  };

  const publicUrl = playlist?.is_public && playlist?.access_code
    ? `${window.location.origin}/p/${playlist.playlist_id}?code=${playlist.access_code}`
    : '';

  // v3.0.145 — mailto: link for one-tap email compose. Preset subject
  // + warm body so recipients get context, not just a naked URL.
  // v3.0.146 — Personalises when a "Send to" name is provided.
  const emailHref = publicUrl
    ? (() => {
        const nm = (recipientName || '').trim();
        const greeting = nm ? `Hey ${nm}!` : 'Hey!';
        const subject = nm
          ? `${nm}, I made you a playlist on Wrapped In Music`
          : `Listen to my playlist "${playlist?.name || 'my playlist'}" on Wrapped In Music`;
        const body = (
          `${greeting}\n\n`
          + `I made a playlist on Wrapped In Music that I wanted you to hear. `
          + `You don't need an account — just tap the link:\n\n`
          + `${publicUrl}\n\n`
          + `Enjoy!\n`
        );
        return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      })()
    : '';

  // v3.0.146 — Comments (owner view)
  const loadComments = async () => {
    setLoadingComments(true);
    try {
      const { data } = await apiFetch(`/playlists/${playlistId}/comments`);
      setComments(Array.isArray(data?.comments) ? data.comments : []);
    } catch (err) {
      // Silent — comments are optional
    } finally {
      setLoadingComments(false);
    }
  };
  useEffect(() => { if (playlistId) loadComments(); }, [playlistId]);

  const handleDeleteComment = async (cid) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await apiFetch(`/playlists/${playlistId}/comments/${cid}`, { method: 'DELETE' });
      setComments((prev) => prev.filter((c) => c.comment_id !== cid));
      toast.success('Comment removed');
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  // v3.0.149 — Rename playlist (share link + access code stay the same)
  const beginRename = () => {
    setRenameValue(playlist?.name || '');
    setRenaming(true);
  };
  const cancelRename = () => setRenaming(false);
  const saveRename = async () => {
    const name = (renameValue || '').trim();
    if (!name) { toast.error('Name cannot be empty'); return; }
    if (name.length > 120) { toast.error('Name too long (max 120)'); return; }
    if (name === playlist?.name) { setRenaming(false); return; }
    setSavingRename(true);
    try {
      const { data } = await apiFetch(`/playlists/${playlistId}`, {
        method: 'PUT',
        body: JSON.stringify({ name }),
      });
      setPlaylist(data);
      setRenaming(false);
      toast.success('Renamed — your share link and code are unchanged');
    } catch (err) {
      toast.error(err.message || 'Rename failed');
    } finally {
      setSavingRename(false);
    }
  };

  // v3.0.144 — drag-reorder (dnd-kit) --------------------------------
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const persistReorder = async (newSongs) => {
    setSavingReorder(true);
    try {
      const { data: updated } = await apiFetch(`/playlists/${playlistId}/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ song_ids: newSongs.map((s) => s.song_id) }),
      });
      setPlaylist(updated);
    } catch (err) {
      toast.error(`Reorder failed: ${err.message || err}`);
      // Revert by reloading from server
      load();
    } finally {
      setSavingReorder(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = songs.findIndex((s) => s.song_id === active.id);
    const newIndex = songs.findIndex((s) => s.song_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(songs, oldIndex, newIndex);
    // Optimistic — commit locally, then persist
    setPlaylist((prev) => ({ ...(prev || {}), songs: next }));
    persistReorder(next);
  };

  // v3.0.144 — custom cover upload -----------------------------------
  const handleCoverPick = () => coverInputRef.current?.click();

  // v3.0.145 — file input just picks; cropper opens next and calls
  // uploadCoverBlob() with the final JPEG blob.
  const handleCoverUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!(file.type || '').startsWith('image/')) {
      toast.error('Please pick an image file (JPG, PNG or WebP)');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Cover image must be under 5 MB');
      e.target.value = '';
      return;
    }
    setCropFile(file);
    setCropOpen(true);
    // Clear input so re-picking the same file re-fires onChange
    e.target.value = '';
  };

  const uploadCoverBlob = async (blob) => {
    setUploadingCover(true);
    try {
      const token = getStoredToken();
      const fd = new FormData();
      fd.append('file', blob, 'cover.jpg');
      const res = await fetch(`${API}/api/playlists/${playlistId}/cover`, {
        method: 'POST',
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.detail || `Upload failed (${res.status})`);
      }
      const data = await res.json();
      setPlaylist((prev) => ({ ...(prev || {}), ...data }));
      toast.success('Cover updated');
    } catch (err) {
      toast.error(err.message || 'Cover upload failed');
      throw err;
    } finally {
      setUploadingCover(false);
    }
  };

  const handleCoverReset = async () => {
    if (!window.confirm('Restore the auto-generated 2x2 collage cover?')) return;
    setResettingCover(true);
    try {
      const { data } = await apiFetch(`/playlists/${playlistId}/cover`, { method: 'DELETE' });
      setPlaylist((prev) => ({ ...(prev || {}), ...data }));
      toast.success('Cover reset — new collage will render shortly');
      // Reload after a short delay so the freshly-built collage URL is picked up
      setTimeout(() => load(), 2500);
    } catch (err) {
      toast.error(err.message || 'Reset failed');
    } finally {
      setResettingCover(false);
    }
  };

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setLinkCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setLinkCopied(false), 1500);
    } catch {
      toast.error('Copy failed — please copy manually');
    }
  };

  const durationTotalSec = useMemo(
    () => songs.reduce((n, s) => n + (Number(s.duration) || 0), 0),
    [songs],
  );

  const fmtDuration = (sec) => {
    if (!sec) return '';
    const total = Math.floor(sec);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const fmtCommentDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  };

  const fmtTotalDuration = (sec) => {
    if (!sec) return '';
    if (sec < 3600) return `${Math.round(sec / 60)} min`;
    const h = Math.floor(sec / 3600);
    const m = Math.round((sec % 3600) / 60);
    return `${h} hr ${m} min`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-400" size={32} />
      </div>
    );
  }

  if (!playlist) {
    return (
      <div className="min-h-screen bg-black text-white">
        <MainNav />
        <div className="max-w-3xl mx-auto pt-24 px-4 text-center">
          <p className="text-zinc-400 mb-4">Playlist not found.</p>
          <Link to="/playlists" className="text-emerald-400 hover:underline">
            Back to my playlists
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-40" data-testid="playlist-detail-page">
      <MainNav />
      <div className="max-w-4xl mx-auto px-4 pt-24 pb-8">
        <Link
          to="/playlists"
          className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft size={16} /> All playlists
        </Link>

        <header className="mb-8 flex items-start gap-5 flex-wrap">
          <div className="relative w-32 h-32 shrink-0 group/cover">
            {playlist.cover_url ? (
              <img
                src={playlist.cover_url}
                alt=""
                className="w-32 h-32 rounded-lg object-cover"
                data-testid="playlist-cover-img"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-32 h-32 bg-gradient-to-br from-emerald-500/30 to-purple-500/30 rounded-lg flex items-center justify-center">
                <ListMusic size={40} className="text-white/60" />
              </div>
            )}
            {/* v3.0.144 — hover to change cover */}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/70 rounded-lg opacity-0 group-hover/cover:opacity-100 transition-opacity">
              <button
                onClick={handleCoverPick}
                disabled={uploadingCover}
                className="text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded flex items-center gap-1.5 disabled:opacity-60"
                data-testid="playlist-cover-upload-btn"
              >
                {uploadingCover ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
                {uploadingCover ? 'Uploading' : 'Change cover'}
              </button>
              {playlist.cover_type === 'custom' && (
                <button
                  onClick={handleCoverReset}
                  disabled={resettingCover}
                  className="text-[10px] text-zinc-300 hover:text-white flex items-center gap-1 disabled:opacity-60"
                  data-testid="playlist-cover-reset-btn"
                >
                  {resettingCover ? <Loader2 size={10} className="animate-spin" /> : <RotateCcw size={10} />}
                  Reset to collage
                </button>
              )}
            </div>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              onChange={handleCoverUpload}
              className="hidden"
              data-testid="playlist-cover-file-input"
            />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-2">
              {playlist.is_public ? (
                <><Globe size={12} /> Public • code {playlist.access_code}</>
              ) : (
                <><Lock size={12} /> Private</>
              )}
            </div>
            <h1 className="text-3xl font-bold mt-1 flex items-center gap-2 group/name">
              {renaming ? (
                <input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value.slice(0, 120))}
                  onBlur={saveRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); saveRename(); }
                    if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                  }}
                  disabled={savingRename}
                  data-testid="playlist-rename-input"
                  className="bg-black/40 border border-emerald-500/40 rounded px-2 py-0.5 text-3xl font-bold text-white focus:outline-none focus:border-emerald-400 min-w-0 flex-1"
                />
              ) : (
                <>
                  <button
                    type="button"
                    onClick={beginRename}
                    className="text-left hover:text-emerald-200 transition-colors"
                    title="Click to rename (share link and code will stay the same)"
                    data-testid="playlist-name-title"
                  >
                    {playlist.name}
                  </button>
                  <button
                    type="button"
                    onClick={beginRename}
                    className="opacity-0 group-hover/name:opacity-100 p-1 rounded hover:bg-white/10 text-zinc-400 hover:text-white transition-opacity"
                    aria-label="Rename playlist"
                    data-testid="playlist-rename-btn"
                  >
                    <Pencil size={16} />
                  </button>
                </>
              )}
            </h1>
            {playlist.description && (
              <p className="text-sm text-zinc-400 mt-2">{playlist.description}</p>
            )}
            <div className="text-xs text-zinc-500 mt-3">
              {songs.length} song{songs.length === 1 ? '' : 's'}
              {durationTotalSec > 0 && <> • {fmtTotalDuration(durationTotalSec)}</>}
            </div>

            {/* Play / Share buttons */}
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              {songs.length > 0 && (
                isLiveQueue ? (
                  <Button
                    onClick={togglePlay}
                    className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-full"
                    data-testid="playlist-play-btn"
                  >
                    {playing ? <Pause size={18} className="mr-1" /> : <Play size={18} className="mr-1" />}
                    {playing ? 'Pause' : 'Play'}
                  </Button>
                ) : (
                  <Button
                    onClick={() => startPlayback(0)}
                    className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-full"
                    data-testid="playlist-play-btn"
                  >
                    <Play size={18} className="mr-1" />
                    Play
                  </Button>
                )
              )}
              <Button
                variant="outline"
                onClick={() => setShareOpen(true)}
                className="border-white/20 text-white hover:bg-white/10 rounded-full"
                data-testid="playlist-share-btn"
              >
                <Share2 size={16} className="mr-1.5" />
                Share
              </Button>
            </div>
          </div>
        </header>

        {songs.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-white/10 rounded-lg">
            <Music className="mx-auto mb-3 text-zinc-600" size={40} />
            <p className="text-zinc-400 mb-4">
              This playlist is empty. Pick songs below or from any song page.
            </p>
            <Link to={`/dashboard?addTo=${playlistId}`}>
              <Button data-testid="playlist-browse-dashboard-btn">
                <Music className="w-4 h-4 mr-1.5" />
                Pick songs to add
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="text-[11px] text-zinc-500 mb-2 flex items-center gap-1.5">
              <GripVertical size={12} /> Drag rows to reorder
              {savingReorder && <Loader2 size={11} className="animate-spin ml-1" />}
            </div>
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={songs.map((s) => s.song_id)}
                  strategy={verticalListSortingStrategy}
                >
                  {songs.map((s, i) => (
                    <SortableSongRow
                      key={s.song_id}
                      song={s}
                      index={i}
                      isNowPlaying={nowPlayingId === s.song_id}
                      isPlaying={playing}
                      onRowClick={() => onRowClick(i)}
                      onRemove={() => handleRemove(s.song_id)}
                      fmtDuration={fmtDuration}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          </>
        )}

        {/* v3.0.146 — Owner Comments section */}
        <section className="mt-10" data-testid="playlist-comments-section">
          <div className="flex items-center gap-2 mb-3 text-sm text-white/80">
            <MessageSquare size={16} className="text-emerald-300" />
            <h3 className="font-medium">Listener comments</h3>
            <span className="text-xs text-zinc-500">{comments.length}</span>
            {playlist?.is_public ? (
              <span className="ml-auto text-[11px] text-zinc-400">
                Anyone with the link can post. Delete anything you don't like.
              </span>
            ) : (
              <span className="ml-auto text-[11px] text-amber-400/80">
                Turn on public sharing to let listeners leave comments.
              </span>
            )}
          </div>
          {loadingComments ? (
            <div className="text-xs text-zinc-500 py-6 text-center">
              <Loader2 size={12} className="inline animate-spin mr-1.5" /> Loading…
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-white/10 rounded-lg">
              <MessageSquare className="mx-auto mb-2 text-zinc-600" size={28} />
              <p className="text-zinc-500 text-sm">No comments yet — share the link and watch this space.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {comments.map((c) => (
                <div
                  key={c.comment_id}
                  className="flex items-start gap-3 p-3 bg-white/5 border border-white/10 rounded group"
                  data-testid={`playlist-comment-${c.comment_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-emerald-300 mb-0.5">
                      <span className="font-medium">{c.author_name}</span>
                      <span className="text-zinc-600">·</span>
                      <span className="text-zinc-500 font-mono">{fmtCommentDate(c.created_at)}</span>
                    </div>
                    <div className="text-sm text-white whitespace-pre-wrap break-words">{c.text}</div>
                  </div>
                  <button
                    onClick={() => handleDeleteComment(c.comment_id)}
                    className="p-1.5 rounded hover:bg-rose-500/20 text-zinc-500 hover:text-rose-300 opacity-60 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                    title="Delete this comment"
                    data-testid={`playlist-comment-delete-${c.comment_id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* v3.0.145 — Cover cropper */}
      <CoverCropperDialog
        open={cropOpen}
        onOpenChange={setCropOpen}
        file={cropFile}
        aspect={1}
        outputSize={1024}
        onCropped={uploadCoverBlob}
      />

      {/* Share dialog (Phase 3) */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>        <DialogContent
          className="bg-zinc-950 border-white/10 text-white max-w-md"
          data-testid="playlist-share-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="text-emerald-400" size={20} />
              Share Playlist
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {playlist.is_public
                ? 'Anyone with this link can listen — the 4-digit code is already baked into the URL. Toggle Public off to revoke every shared link instantly.'
                : 'Turn on sharing to get a link that already contains its 4-digit access code — anyone you send it to can listen without signing in.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded px-3 py-2">
              <div className="text-sm">
                <div className="font-medium text-white">Public link</div>
                <div className="text-xs text-zinc-500">
                  {playlist.is_public ? 'On — anyone with the full URL can listen' : 'Off — only you can see this'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => togglePublic(!playlist.is_public)}
                disabled={savingShare}
                data-testid="playlist-share-toggle"
                className={`relative inline-flex items-center h-6 w-11 shrink-0 rounded-full transition-colors ${
                  playlist.is_public ? 'bg-emerald-500' : 'bg-zinc-700'
                } ${savingShare ? 'opacity-60 cursor-wait' : ''}`}
              >
                <span
                  className={`inline-block h-5 w-5 bg-white rounded-full transform transition-transform ${
                    playlist.is_public ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {playlist.is_public && (
              <>
                <div className="bg-white/5 border border-white/10 rounded p-3 grid grid-cols-3 gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Code</div>
                    <div className="text-lg font-mono tracking-widest text-emerald-300" data-testid="playlist-access-code">
                      {playlist.access_code}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1"
                      title="All-time count of every time this share link was opened (any visitor, including refreshes)"
                    >
                      All-time plays
                    </div>
                    <div
                      className="text-lg font-mono text-white flex items-center gap-1"
                      data-testid="playlist-play-count"
                    >
                      <Headphones size={14} className="text-emerald-300" />
                      {playlist.play_count ?? 0}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1"
                      title="Distinct listeners in the last 30 days, counted by hashed IP"
                    >
                      Unique (30d)
                    </div>
                    <div
                      className="text-lg font-mono text-white flex items-center gap-1"
                      data-testid="playlist-unique-listeners"
                    >
                      <Users size={14} className="text-emerald-300" />
                      {playlist.unique_listeners_30d ?? 0}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Share link</div>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={publicUrl}
                      onClick={(e) => e.target.select()}
                      data-testid="playlist-share-url"
                      className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-white focus:outline-none"
                    />
                    <Button
                      variant="outline"
                      onClick={copyLink}
                      className="border-white/20 text-white hover:bg-white/10 shrink-0"
                      data-testid="playlist-share-copy"
                    >
                      {linkCopied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                      {linkCopied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>

                  {/* v3.0.146 — Recipient name + email + QR toggle */}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <input
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value.slice(0, 40))}
                      placeholder="Send to (name — optional)"
                      data-testid="playlist-share-recipient"
                      className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-white focus:outline-none placeholder:text-zinc-500"
                    />
                    <a
                      href={emailHref}
                      className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 hover:underline"
                      data-testid="playlist-share-email"
                    >
                      <Mail size={12} /> Email
                    </a>
                    <button
                      type="button"
                      onClick={() => setShowQr((v) => !v)}
                      className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white"
                      data-testid="playlist-share-qr-toggle"
                    >
                      <QrCode size={12} /> {showQr ? 'Hide QR' : 'Show QR'}
                    </button>
                  </div>

                  {showQr && (
                    <div
                      className="mt-3 flex flex-col items-center gap-2 bg-white p-3 rounded-md w-fit mx-auto"
                      data-testid="playlist-share-qr"
                    >
                      <QRCodeSVG value={publicUrl} size={160} level="M" includeMargin={false} />
                      <div className="text-[10px] text-zinc-600 text-center">Scan to listen instantly</div>
                    </div>
                  )}
                </div>

                {/* v3.0.146 — Daily plays heatmap (last 30 days) */}
                {Array.isArray(playlist.plays_daily_30d) && playlist.plays_daily_30d.length > 0 && (
                  <div data-testid="playlist-plays-heatmap" className="mt-2">
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">
                      Last 30 days
                    </div>
                    <div className="h-24 bg-black/30 rounded p-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={playlist.plays_daily_30d} margin={{ top: 2, right: 4, bottom: 2, left: 0 }}>
                          <XAxis
                            dataKey="date"
                            tickFormatter={(d) => d.slice(5)}
                            tick={{ fill: '#71717a', fontSize: 9 }}
                            interval={4}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis hide />
                          <Tooltip
                            cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                            contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: 11 }}
                            labelStyle={{ color: '#a1a1aa' }}
                            formatter={(v, k) => [`${v}`, k === 'opens' ? 'Plays' : 'Unique']}
                          />
                          <Bar dataKey="opens" fill="#34d399" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// v3.0.144 — dnd-kit sortable row
function SortableSongRow({ song, index, isNowPlaying, isPlaying, onRowClick, onRemove, fmtDuration }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: song.song_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : 'auto',
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 px-3 py-2.5 border-b border-white/5 last:border-b-0 group hover:bg-white/5 cursor-pointer ${isNowPlaying ? 'bg-emerald-500/10' : ''} ${isDragging ? 'bg-white/5 ring-1 ring-emerald-400/40' : ''}`}
      onClick={onRowClick}
      data-testid={`playlist-song-${index}`}
    >
      <button
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        aria-label="Drag to reorder"
        className="p-1 text-zinc-600 hover:text-zinc-300 cursor-grab active:cursor-grabbing touch-none"
        data-testid={`playlist-drag-handle-${index}`}
      >
        <GripVertical size={14} />
      </button>
      <div className="w-6 text-center text-xs text-zinc-500 font-mono">
        {isNowPlaying && isPlaying ? (
          <div className="flex justify-center gap-0.5">
            <span className="w-0.5 h-3 bg-emerald-400 animate-pulse" />
            <span className="w-0.5 h-3 bg-emerald-400 animate-pulse" style={{ animationDelay: '150ms' }} />
            <span className="w-0.5 h-3 bg-emerald-400 animate-pulse" style={{ animationDelay: '300ms' }} />
          </div>
        ) : (index + 1)}
      </div>
      {song.image_url ? (
        <img
          src={song.image_url}
          alt=""
          className="w-10 h-10 rounded object-cover"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      ) : (
        <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center">
          <Music size={16} className="text-zinc-500" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className={`text-sm truncate ${isNowPlaying ? 'text-emerald-300 font-medium' : 'text-white'}`}>
          {song.title || 'Untitled'}
        </div>
        {song.locked && (
          <div className="text-[10px] text-amber-400 flex items-center gap-1">
            <Lock size={10} /> Locked — unlock to play
          </div>
        )}
      </div>
      {song.duration && (
        <div className="text-xs text-zinc-500 font-mono shrink-0">
          {fmtDuration(song.duration)}
        </div>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-rose-500/20 text-zinc-400 hover:text-rose-300 transition-opacity"
        title="Remove from playlist"
        data-testid={`playlist-remove-song-${index}`}
      >
        <X size={14} />
      </button>
    </div>
  );
}
